import React, {
	createRef,
	useEffect,
	useState,
	isValidElement,
	cloneElement as cloneNode,
} from 'react';
import { render as renderToast } from 'react-dom';
import { createAppend, css, is, uid } from 'Common/Utils';

class Toast {
	target;
	options;

	static #container = createAppend(document.body, 'div', {
		position: 'fixed',
		zIndex: 2000,
		top: 0,
		left: '50%',
		transform: 'translateX(-50%)',
		display: 'flex',
		flexDirection: 'column',
		justifyContent: 'flex-end',
		alignItems: 'center',
		width: 'max-width: 658px',
		padding: '0 15px'
	});

	static #collection = [];

	static #remove = (key) => {
		this.#collection = this.#collection.filter((toast) => toast.key !== key);
	};

	static create(...props) {
		return new Toast(...props);
	}

	static Toaster = ({ as: Component = 'div', options, message, onExit }) => {
		const [state, setState] = useState({ mounted: false, show: true });

		const targetRef = createRef();

		useEffect(() => {
			const cleanup = []
			const target = targetRef.current;
			if (!target) return;
			if(!state.mounted) {
				setState({ mounted: true, show: true });
			}
			const {
				duration,
				timing,
				offset: [offsetX = 0, offsetY = 10],
			} = options;

			css(target, {
				// is entering
				marginTop: `${offsetY}px`,
				marginLeft: `${offsetX}px`,
				opacity: 1,
				maxHeight: `${target.offsetHeight}px`,
			});

			cleanup.push(setTimeout(() => {
				// is Exiting
				css(target, {
					opacity: 0,
					maxHeight: 0,
				});

				cleanup.push(setTimeout(() => {
					setState({ mounted: true, show: false });
					onExit();
				}, timing)); // is Exited
			}, duration));

			return () => cleanup.forEach(value => clearTimeout(value))
		}, [targetRef, options, onExit, state.mounted]);

		return (
			state.show && (
				<Component
					ref={targetRef}
					style={
						Component === 'div'
							? {		
									padding: '10px 16px',
									fontSize: '14px',
									borderRadius: '2px',
									backgroundColor: '#fff',
									opacity: 0,
									color: 'rgba(0,0,0,0.65)',
									boxShadow:
										'0 3px 6px -4px rgba(0,0,0,.12), 0 6px 16px 0 rgba(0,0,0,.08), 0 9px 28px 8px rgba(0,0,0,.05)',
									transition:
										'opacity 300ms ease-out, max-height 200ms ease-in 300ms',
							  }
							: undefined
					}>
					{message}
				</Component>
			)
		);
	};

	constructor(message, props) {
		const defaultOpt = {
			placement: 'top',
			offset: [0, 10],
			duration: 5000,
			timing: 500,
		};

		const { target, key, options = {} } =
			is.obj(props)
				? props
				: {};
		if (
			key &&
			Toast.#collection.find(
				(toast) => toast.key === key
			)
		) {
			return Toast.#update({ message, ...props, options: { ...defaultOpt, ...options } })
		}

		Toast.#collection.push(
			<Toast.Toaster
				key={key || uid()}
				message={message}
				as={target}
				options={{ ...defaultOpt, ...options }}
				onExit={() => Toast.#remove(key)}
			/>
		);

		Toast.#update();
	}

	static #update = (props) => {
		renderToast(
			<>
				{ props ? Toast.#collection.map((toast) =>  props.key === toast.key ? cloneNode(toast, { ...toast.props, ...props}) : toast)
				: Toast.#collection}
			</>,
			Toast.#container
		);
	};
}

export default Toast;
